import React, { useMemo } from "react";

import { useToggleAll } from "../../../lib/hooks/useToggleAll";
import {
  buildDeviceTree,
  DeviceTreeDevice,
  DeviceTreeNode,
  toggleNodeSelection,
} from "./lib/deviceTree";

import { WorkplaceFields } from "../../../api/graphql/workplaces";
import { WorkspacePicker } from "../../shared/WorkspacePicker/WorkspacePicker";
import { ToggleAllButton } from "../../shared/ToggleAllButton/ToggleAllButton";
import { Button } from "../../shared/Button/Button";
import { Icon } from "../../shared/Icon/Icon";
import { DeviceTreeLevel } from "./DeviceTreeLevel";

import "./DeviceTimePicker.scss";

interface DeviceTreeProps {
  rootWorkplaces: WorkplaceFields[][];
  devices: DeviceTreeDevice[];
  selectedIds: ReadonlySet<string>;
  onToggleSelection: (node: DeviceTreeNode) => void;
  allToggled: boolean;
}

const DeviceTree = ({
  rootWorkplaces,
  devices,
  ...levelProps
}: DeviceTreeProps) => {
  const { hierarchy, unassigned } = useMemo(
    () => buildDeviceTree(rootWorkplaces, devices),
    [rootWorkplaces, devices]
  );

  return (
    <>
      <h4 className="DeviceTimePicker__heading">Device hierarchy</h4>
      {hierarchy.map((node) => (
        <DeviceTreeLevel key={node.id} node={node} {...levelProps} />
      ))}
      {unassigned ? <DeviceTreeLevel node={unassigned} {...levelProps} /> : null}
    </>
  );
};

interface Props {
  // The account's compatible devices; the only ones the picker lists
  devices: DeviceTreeDevice[];
  selectedDevices: DeviceTreeDevice[];
  onChange: (devices: DeviceTreeDevice[]) => void;
  onClose: () => void;
}

export const DeviceTimePicker = ({
  devices,
  selectedDevices,
  onChange,
  onClose,
}: Props) => {
  const { allToggled, handleToggleAll } = useToggleAll(true);
  const selectedIds = new Set(selectedDevices.map((device) => device.id));

  const handleToggleSelection = (node: DeviceTreeNode) =>
    onChange(toggleNodeSelection(selectedDevices, node));

  return (
    <WorkspacePicker
      title="Select devices"
      message="Pick the devices whose date and time settings you want to change"
      description="Browse the workspace hierarchy to select devices"
      closePicker={onClose}
      sidebarClassName="DeviceTimePicker"
      footer={
        <div className="pt-1 WayFinderWorkspaceFooterPicker">
          <div className="DeviceTimePicker__warning flex-a-center">
            <Icon icon="warning-triangle" />
            <span>Only supported devices will be listed</span>
          </div>
          <div className="flex-a-center justify-content-between WayFinderWorkspaceFooterPicker__buttons">
            <Button
              title="Clear"
              size="small"
              color="outline-secondary"
              onClick={() => onChange([])}
            />
            <Button
              title="Confirm"
              size="small"
              color="primary"
              onClick={onClose}
            />
          </div>
        </div>
      }
    >
      {({ rootWorkplaces }) => (
        <>
          <ToggleAllButton
            allToggled={allToggled}
            onToggleAll={handleToggleAll}
          />
          <DeviceTree
            rootWorkplaces={rootWorkplaces}
            devices={devices}
            selectedIds={selectedIds}
            onToggleSelection={handleToggleSelection}
            allToggled={allToggled}
          />
        </>
      )}
    </WorkspacePicker>
  );
};
